import { StyledBackdrop } from "@rentiohq/web-shared/dist/components/TextField/TextField.styled";
import styled, { EFontSizes } from "@rentiohq/web-theme";
import { spacing } from "@rentiohq/web-theme/dist/utils/spacing";
import { STAR_FILLED_COLOR } from "../../../../components/Rating";

/**
 * Visit mode (DEV-3227).
 *
 * Every value here comes from the Rentio theme. The Rentmatch scene next door
 * keeps its own RM_COLORS map of hardcoded hex values; this screen deliberately
 * does not use it, because those hexes are copies of these same tokens and the
 * copy is the thing that drifts.
 *
 * Laid out for a phone held in one hand: a single column that fills a small
 * screen and stays centered on a large one, and touch targets big enough to hit
 * without looking.
 */

/** Minimum size of anything meant to be tapped rather than clicked. */
const TAP_TARGET = "44px";

/** Long enough to read as a movement, short enough not to be waited on. */
const SLIDE_DURATION = "200ms";

export const Frame = styled.div`
  /* A bounded height, not a minimum: the body scrolls inside it and the bulk
     bar stays pinned to the bottom of the screen rather than to the bottom of
     the list. dvh rather than vh because on a phone vh counts the browser's own
     bars as if they were not there, which pushed that bar off the screen. */
  height: 100vh;
  height: 100dvh;
  display: flex;
  justify-content: center;
  background: ${({ theme }) => theme.colors.neutral10};
`;

export const Column = styled.div`
  flex: 1;
  /* Without this a flex item refuses to go narrower than its widest content,
     so a long strip of day tabs stretched the column past the screen and the
     sideways scroll on that strip had nothing left to scroll inside. */
  min-width: 0;
  max-width: 480px;
  display: flex;
  flex-direction: column;
  background: ${({ theme }) => theme.colors.neutral0};

  @media (min-width: 481px) {
    border-left: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
    border-right: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
  }
`;

export const Header = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${spacing("tight")};
  padding: ${spacing("base")};
  border-bottom: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
`;

export const HeaderTitles = styled.div`
  min-width: 0;
`;

export const HeaderTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.neutral70};
`;

export const HeaderDay = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  color: ${({ theme }) => theme.colors.neutral70};
`;

export const HeaderAddress = styled.div`
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Medium]}px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral100};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* ── Day tabs ──────────────────────────────────────────────────────────── */

export const DayTabs = styled.div`
  display: flex;
  /* Out to the edges of the list it sits in, which pads itself all round. */
  margin: -${spacing("base")} -${spacing("base")} ${spacing("base")};
  padding: 0 ${spacing("tight")};
  border-bottom: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
  /* The strip scrolls sideways on a narrow screen; hiding the bar keeps it
     from stealing vertical room on a list that is already tight. */
  overflow-x: auto;
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

/** Drawn as the property's own tabs are: uppercase, underlined when active. */
export const DayTab = styled.button<{ $isActive: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${spacing("tight")};
  padding: ${spacing("base")};
  border: none;
  border-bottom: 2px solid
    ${({ theme, $isActive }) =>
      $isActive ? theme.getColor("primary") : "transparent"};
  background: transparent;
  cursor: pointer;
  white-space: nowrap;
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  font-weight: 600;
  color: ${({ theme, $isActive }) =>
    $isActive ? theme.getColor("primary") : theme.getColor("gray", "400")};
`;

/**
 * How many candidates that day still has nothing recorded for, drawn as the
 * count beside a property tab: a small square, white on gray, green once the
 * tab is the open one.
 */
export const DayTabCount = styled.span<{ $isActive: boolean }>`
  margin: -2px 0;
  padding: 2px 3px 2px 4px;
  border-radius: 2px;
  background: ${({ theme, $isActive }) =>
    $isActive ? theme.getColor("primary") : theme.getColor("gray", "400")};
  color: ${({ theme }) => theme.colors.neutral0};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
`;

/* ── Time blocks ───────────────────────────────────────────────────────── */

export const Body = styled.div`
  flex: 1;
  /* The same rule, vertically: without it the list refuses to be shorter than
     its content and scrolls the page instead of itself. */
  min-height: 0;
  overflow-y: auto;
  padding: ${spacing("base")};
`;

export const Block = styled.div`
  margin-bottom: ${spacing("loose")};
`;

export const BlockHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing("tight")};
  margin-bottom: ${spacing("tight")};
`;

/**
 * The clock beside the hour. It says nothing the digits do not, and is drawn in
 * the quiet tone of a label for exactly that reason: it marks where a block
 * starts for an eye running down the list.
 */
export const BlockTimeGroup = styled.span`
  min-width: 0;
  display: flex;
  align-items: center;
  gap: ${spacing("extraTight")};
  color: ${({ theme }) => theme.colors.neutral60};
`;

export const BlockTime = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Medium]}px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.neutral100};
`;

export const BlockCount = styled.span`
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  color: ${({ theme }) => theme.colors.neutral70};
`;

/* ── Candidate card ────────────────────────────────────────────────────── */

export const Card = styled.div<{ $isDone: boolean; $isOpen: boolean }>`
  margin-bottom: ${spacing("tight")};
  /* Outlined while it is open: of a dozen cards on a busy day this is the one
     being worked on, and the edge says so without a word. */
  border: 1px solid
    ${({ theme, $isOpen }) =>
      $isOpen ? theme.getColor("primary") : theme.getColor("gray", "200")};
  border-radius: ${({ theme }) => theme.borderRadius()};
  /* Nothing marks what still has to be done. What is done steps back instead,
     so the ones still standing out are the ones left to handle. An open card
     returns to white whatever its state: it is the one being read. */
  background: ${({ theme, $isDone, $isOpen }) =>
    $isDone && !$isOpen ? theme.getColor("gray", "0") : theme.colors.neutral0};
  overflow: hidden;
`;

export const CardSummary = styled.button`
  width: 100%;
  min-height: ${TAP_TARGET};
  display: flex;
  align-items: center;
  gap: ${spacing("tight")};
  /* Even on all four sides, so the chevron sits as far from the edge as the
     name sits from the top. */
  padding: ${spacing("base")};
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
`;

export const Chevron = styled.span<{ $isOpen: boolean }>`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.neutral70};
  transform: rotate(${({ $isOpen }) => ($isOpen ? "180deg" : "0deg")});
  transition: transform 0.15s ease;
`;

/** Initials, the way a person is marked everywhere a photo is missing. */
export const Avatar = styled.span<{ $isDone: boolean }>`
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: ${({ theme, $isDone }) =>
    $isDone ? theme.getColor("gray", "200") : theme.getColor("primary", "0")};
  color: ${({ theme, $isDone }) =>
    $isDone ? theme.colors.neutral70 : theme.getColor("primary", "1000")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  font-weight: 700;
  text-transform: uppercase;
`;

/**
 * Wraps rather than truncates. A half-read name is worse than a second line:
 * this screen is a broker matching a face at the door to a row on a phone, and
 * two candidates in a family often share a surname.
 */
export const CandidateName = styled.span<{ $isDone: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  font-weight: 600;
  color: ${({ theme, $isDone }) =>
    $isDone ? theme.colors.neutral70 : theme.colors.neutral100};
  overflow-wrap: anywhere;
`;

/** The score in one glyph's worth of width, for a row the name owns. */
export const ScoreBadge = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  gap: ${spacing("extraTight")};
  padding: ${spacing("extraTight")} ${spacing("tight")};
  /* Rounded, not round: at a full radius the taller box turned into a pill. */
  border-radius: ${({ theme }) => theme.borderRadius("large")};
  border: 1px solid ${({ theme }) => theme.getColor("yellow", "400")};
  background: ${({ theme }) => theme.getColor("yellow", "0")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  font-weight: 700;
  line-height: 1;
  color: ${({ theme }) => theme.colors.neutral90};

  /* A pill rather than a bare glyph: at this size a lone star reads as
     punctuation, and something drawn around it reads as a value. */
  &::after {
    content: "★";
    color: ${STAR_FILLED_COLOR};
    /* Larger than the number beside it: the star is the thing that carries the
       color, and at matching sizes it reads as the smaller of the two. */
    font-size: 15px;
    line-height: 1;
  }
`;

export const SummaryMain = styled.span`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${spacing("extraTight")};
`;

/**
 * Score, warning and chevron, held together at the end of the row. They belong
 * to the card as a whole, not to its first line, so they center against both
 * lines rather than riding up with the name when a status label appears under
 * it.
 */
export const SummaryEnd = styled.span`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: ${spacing("tight")};
`;

export const SummaryRight = styled.span`
  max-width: 100%;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing("tight")};
`;

export const StatusLabel = styled.span<{ $variant: "invited" | "noShow" }>`
  display: inline-flex;
  align-items: center;
  gap: ${spacing("extraTight")};
  padding: 0 ${spacing("tight")};
  border-radius: ${({ theme }) => theme.borderRadius()};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  font-weight: 600;
  background: ${({ theme, $variant }) =>
    $variant === "invited"
      ? theme.getColor("green", "0")
      : theme.getColor("gray", "0")};
  color: ${({ theme, $variant }) =>
    $variant === "invited"
      ? theme.getColor("green", "1000")
      : theme.colors.neutral80};
`;

export const CardBody = styled.div`
  min-width: 0;
  border-top: 1px solid ${({ theme }) => theme.getColor("gray", "200")};

  /* The button centres its content as a block but never says how the lines
     inside it align, which shows only once a label is long enough to wrap. The
     invite label on this card is. */
  button {
    text-align: center;
  }
`;

/**
 * One part of an opened card. Facts, contact details and judgement are three
 * different kinds of thing, and set in a single column they read as one wall of
 * text. A hairline between them, and a tint behind the first, lets the eye land
 * on the part it came for.
 */
export const Section = styled.div<{ $isShaded?: boolean }>`
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: ${spacing("base")};
  padding: ${spacing("base")};
  background: ${({ theme, $isShaded }) =>
    $isShaded ? theme.getColor("gray", "0") : "transparent"};

  & + & {
    border-top: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
  }
`;

/** What the overview leads with, first thing on an opened card. */
export const Facts = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing("base")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  color: ${({ theme }) => theme.colors.neutral80};
`;

export const Income = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral100};
`;

/**
 * Two columns: how to reach this person on the left, where to read about them
 * on the right. They wrap under each other when the card is too narrow to hold
 * both, which on a phone it usually is.
 */
export const ContactRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  column-gap: ${spacing("base")};
  row-gap: ${spacing("tight")};
`;

export const ContactLinks = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
`;

/** The profile links, set against the right edge of the card. */
export const ProfileLinks = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing("tight")};
`;

/**
 * An icon with no label needs to look like something you press. A tinted square
 * says that quietly, which is the right volume: these two lead out of the
 * screen, they are not the work being done on it, and knocked out white on dark
 * they carried more weight than the buttons below them.
 */
export const ProfileLink = styled.a`
  flex-shrink: 0;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
  border-radius: ${({ theme }) => theme.borderRadius()};
  background: ${({ theme }) => theme.getColor("gray", "0")};
  color: ${({ theme }) => theme.colors.neutral80};
  text-decoration: none;
`;

/**
 * A box of our own around every icon on this screen. An icon is a fixed mark,
 * never content to be squeezed, and on a phone every row is narrow enough that
 * something has to give. Saying so through the icon component's own styled
 * class does not work from here: that lives in another package with its own
 * styled-components instance, so the selector resolves to a class that is
 * never in the DOM.
 */
export const IconBox = styled.span`
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;

  /* And nothing inside it gives either, so the glyph keeps the square it was
     drawn in rather than being pressed into whatever width is left. */
  > * {
    flex-shrink: 0;
  }

  /* The icon component sets a width on its svg and no height, leaving the
     height to be read off the viewBox. Chrome does that; Safari on a phone does
     not, and falls back to a height of its own, which is why these looked right
     on a laptop and squashed into a square on a phone. Pinning both axes leaves
     the glyph to fit inside them, which is what preserveAspectRatio does by
     default. */
  svg {
    height: 100%;
  }
`;

/** A plain gray link, the way a phone number and an address are shown on the
 * contact dashboard. Not a button: neither of them is an action on this card. */
export const ContactLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: ${spacing("extraTight")};
  /* Tall enough for a thumb, no taller: these two sit under each other, and
     the 44px a standalone target wants left them reading as two unrelated
     lines rather than as one person's details. */
  min-height: ${spacing("loose")};
  /* neutral60, the tone the contact page draws these in, and the tone our icon
     falls back to when nothing sets one. */
  color: ${({ theme }) => theme.colors.neutral60};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  text-decoration: none;
  /* An email address holds no spaces, so it has nowhere to break and will push
     a card wider than the phone it is on rather than wrap. It may break
     anywhere, and the link may be narrower than it. */
  min-width: 0;
  overflow-wrap: anywhere;
`;

/**
 * The shared field pads its wrapper rather than the textarea inside it, so a
 * textarea long enough to scroll puts its scrollbar that far in from the edge.
 * The textarea is widened into that padding and given its inset back as its
 * own, which puts the scrollbar where the border is.
 *
 * Its border is toned down to match the buttons around it: the shared field
 * draws one a shade darker than everything else on the card.
 */
export const NoteField = styled.div`
  ${StyledBackdrop} {
    border-color: ${({ theme }) => theme.colors.neutral40};
  }

  textarea {
    margin-right: -${({ theme }) => theme.inputStyles.spacing.horizontal};
    padding-right: ${({ theme }) => theme.inputStyles.spacing.horizontal};
  }
`;

/* ── Visit labels ──────────────────────────────────────── */

export const LabelRow = styled.div`
  min-width: 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: ${spacing("extraTight")};
`;

/**
 * One tint for all of them, on purpose: a label sorted into a colored bin is a
 * verdict recorded about a person, and these are observations. Picked or not is
 * carried by the check and the fill, which is the one thing that does have to
 * read at arm's length, on a phone, in a hallway.
 */
export const LabelChip = styled.button<{ $isSelected: boolean }>`
  flex-shrink: 0;
  max-width: 100%;
  min-height: 28px;
  display: inline-flex;
  align-items: center;
  gap: ${spacing("extraTight")};
  padding: 0 ${spacing("tight")};
  border-radius: 999px;
  border: 1px solid
    ${({ theme, $isSelected }) =>
      $isSelected
        ? theme.getColor("primary", "200")
        : theme.getColor("gray", "200")};
  background: ${({ theme, $isSelected }) =>
    $isSelected ? theme.getColor("primary", "0") : theme.colors.neutral0};
  color: ${({ theme, $isSelected }) =>
    $isSelected ? theme.getColor("primary", "1000") : theme.colors.neutral70};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  font-weight: ${({ $isSelected }) => ($isSelected ? 600 : 400)};
  cursor: pointer;
  text-align: left;
  overflow-wrap: anywhere;
`;

/** The one that opens a field rather than picking a word. */
export const LabelAddChip = styled(LabelChip)`
  border-style: dashed;
`;

export const LabelInput = styled.input`
  width: 140px;
  min-width: 0;
  height: 28px;
  padding: 0 ${spacing("tight")};
  box-sizing: border-box;
  border: 1px solid ${({ theme }) => theme.getColor("gray", "400")};
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.neutral0};
  color: ${({ theme }) => theme.colors.neutral100};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  outline: none;
`;

export const FieldLabel = styled.div`
  margin-bottom: ${spacing("extraTight")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral70};
`;

/**
 * No-show closes the card, so it sits last and stays quiet: a plain link rather
 * than a button, to keep it from being hit while reaching for the stars.
 */
/**
 * The same button as in the candidate list, down to the values: neutral40 and
 * neutral90 are the exact hexes that list hardcodes in its own color map. Only
 * the corners are rounded here, where the table needed square ones to sit flush
 * against its cells.
 */
export const NoShowButton = styled.button`
  align-self: center;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing("extraTight")};
  min-width: 120px;
  height: 36px;
  padding: 0 ${spacing("tight")};
  box-sizing: border-box;
  line-height: 1;
  border: 1px solid ${({ theme }) => theme.colors.neutral40};
  border-radius: ${({ theme }) => theme.borderRadius()};
  background: ${({ theme }) => theme.colors.neutral0};
  color: ${({ theme }) => theme.colors.neutral90};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
`;

/* ── Bulk bar ──────────────────────────────────────────────────────────── */

/**
 * The room the bar takes, which is none while it is away. A grid row is what
 * animates here: height itself does not, and unmounting the bar would make it
 * disappear rather than leave.
 */
export const BulkSlot = styled.div<{ $isVisible: boolean }>`
  flex-shrink: 0;
  display: grid;
  grid-template-rows: ${({ $isVisible }) => ($isVisible ? "1fr" : "0fr")};
  transition: grid-template-rows ${SLIDE_DURATION} ease;
  /* Nothing to press, and nothing to tab into, while it is gone. */
  pointer-events: ${({ $isVisible }) => ($isVisible ? "auto" : "none")};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;

/** Holds the bar to the room above, so it leaves the screen rather than the
 * page while it slides. */
export const BulkClip = styled.div`
  overflow: hidden;
`;

export const BulkBar = styled.div<{ $isVisible: boolean }>`
  flex-shrink: 0;
  transform: translateY(${({ $isVisible }) => ($isVisible ? "0" : "100%")});
  transition: transform ${SLIDE_DURATION} ease;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  display: flex;
  flex-direction: column;
  align-items: center;
  /* Clears the home indicator on a phone that has one. */
  padding: ${spacing("tight")} ${spacing("base")}
    calc(${spacing("base")} + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
  background: ${({ theme }) => theme.colors.neutral0};
`;

/** What the bulk button will do, for a broker who has never pressed it. */
export const BulkHint = styled.div`
  margin-top: ${spacing("extraTight")};
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  color: ${({ theme }) => theme.colors.neutral70};
`;

export const RetryAction = styled.div`
  margin-top: ${spacing("base")};
`;

/** The sentence a confirmation turns on, at reading size. */
export const ConfirmBody = styled.p`
  margin: 0;
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Medium]}px;
  color: ${({ theme }) => theme.colors.neutral100};
`;

export const EmptyDay = styled.div`
  padding: ${spacing("extraLoose")} 0;
  text-align: center;
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  color: ${({ theme }) => theme.colors.neutral70};
`;

/* ── Invite selection ──────────────────────────────────────────────────── */

export const SelectionIntro = styled.p`
  margin: 0 0 ${spacing("base")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Normal]}px;
  color: ${({ theme }) => theme.colors.neutral80};
`;

/**
 * No vertical padding: the option list lays its checkbox and this label out
 * from the top, so any padding here drops the name below the box beside it.
 */
export const SelectionRow = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing("tight")};
  cursor: pointer;
`;

export const SelectionLeft = styled.span`
  display: flex;
  align-items: center;
  gap: ${spacing("tight")};
  min-width: 0;
`;

export const SkippedTitle = styled.div`
  margin-top: ${spacing("base")};
  padding-top: ${spacing("tight")};
  border-top: 1px solid ${({ theme }) => theme.getColor("gray", "200")};
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.neutral70};
`;

export const SkippedRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${spacing("tight")};
  padding: ${spacing("extraTight")} 0;
  font-size: ${({ theme }) => theme.fontSizes[EFontSizes.Small]}px;
  color: ${({ theme }) => theme.colors.neutral70};
`;
